import {createSelector} from 'reselect';
import qs from 'query-string';
import {Map} from 'immutable';

import {operatingSystem} from '../../constants';
import {
    FEATURE_FLAGS_ENVIRONMENT_VARIABLES_PREFIX,
    FEATURE_FLAGS_QUERY_STRING_ENABLED,
    FEATURE_FLAGS_QUERY_STRING_KEY
} from '../../constants/FeatureFlag';
import {toBoolean, toScreamingSnakeCase} from '../../utils/GeneralUtils';

export const selectIsLauncher = state => state.getIn(['app', 'isLauncher']);
export const selectTheme = state => state.getIn(['app', 'theme']);
export const selectPlatform = state => state.getIn(['app', 'platform']);
export const selectIsOnline = state => state.getIn(['app', 'isOnline']);
export const selectAppEpicSid = state => state.getIn(['app', 'epicSid']);

export const selectShouldDisableHeavyTransitions = createSelector(
    selectIsLauncher,
    state => state.getIn(['app', 'platform']),
    (isLauncher, platform) => isLauncher && platform === operatingSystem.name.MAC
);

export const selectIsWindows = createSelector(
    selectPlatform,
    platform => platform === operatingSystem.name.WINDOWS || platform === operatingSystem.name.WIN32
);

export const selectRouterLocation = state => state.getIn(['router', 'location']);

export const selectAppEnv = state => state.getIn(['app', 'env']);

/*

Parses out a comma-separated list of feature flags read from a query string.
Then converts into an immutable `Map` of truthy values where keys use screaming
snake case and are prefixed with `FEATURE_FLAGS_ENVIRONMENT_VARIABLES_PREFIX`.

If the current location search is `?ff=free-games`, this will parse it to an
immutable Map with the given structure:

  {
    EPIC_CLIENT_FEATURE_FLAG_FREE_GAMES: true
  }

*/
const parseFeatureFlagsFromQueryString = location => {
    if (!location || !location.get('search')) return Map();

    const search: string = location.get('search') || '';
    const params: Record<string, string | string[]> = qs.parse(search);
    const hasFeatureFlags: boolean =
        Boolean(search && search.length) &&
        Object.keys(params).includes(FEATURE_FLAGS_QUERY_STRING_KEY);

    if (!hasFeatureFlags || Array.isArray(params.ff)) return Map();

    const prefix: string = FEATURE_FLAGS_ENVIRONMENT_VARIABLES_PREFIX.concat('_');

    return params.ff
        .split(',')
        .reduce(
            (accumulator, current) =>
                accumulator.set(prefix.concat(toScreamingSnakeCase(current)), true),
            Map()
        );
};

const parseFeatureFlagsFromEnvironmentVariables = environmentVariables => {
    if (!environmentVariables) return Map();

    return environmentVariables
        .filter((_, key) => key.startsWith(FEATURE_FLAGS_ENVIRONMENT_VARIABLES_PREFIX))
        .map(toBoolean);
};

export const selectFeatureFlags = createSelector(
    selectAppEnv,
    selectRouterLocation,
    (env, location) => {
        const fromEnvironmentVariables = parseFeatureFlagsFromEnvironmentVariables(
            env && env.remove(FEATURE_FLAGS_QUERY_STRING_ENABLED)
        );

        const shouldEnableFeatureFlagsFromQueryString =
            env && env.get(FEATURE_FLAGS_QUERY_STRING_ENABLED);

        const fromQueryString = shouldEnableFeatureFlagsFromQueryString
            ? parseFeatureFlagsFromQueryString(location)
            : Map();

        return fromEnvironmentVariables.merge(fromQueryString);
    }
);

// Takes in the application store and a slug representing a feature flag.
export const isFeatureFlagOn = (state, flag: string): boolean => {
    const flags = selectFeatureFlags(state);
    const prefix: string = FEATURE_FLAGS_ENVIRONMENT_VARIABLES_PREFIX.concat('_');
    const key: string = prefix.concat(toScreamingSnakeCase(flag));

    if (!flags.has(key)) return false;

    return flags.get(key) === true;
};
